import axios from 'axios';

// Configure axios defaults
const getApiBaseUrl = () => {
  // Prefer explicit env var if set (from .env / .env.production / .env.development)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Check if we're in development mode
  if (
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'development'
  ) {
      return 'http://localhost:5000/api/inbound_workflow';
  }
  // Dev (default )
  return 'http://ec2-44-197-231-239.compute-1.amazonaws.com:5000/api/inbound_workflow';
};

const API_BASE_URL = getApiBaseUrl();

console.log('API_BASE_URL:', API_BASE_URL);
console.log('NODE_ENV:', process.env.NODE_ENV);
console.log('REACT_APP_ENV:', process.env.REACT_APP_ENV);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth if needed
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      console.error('Unauthorized: Session expired or invalid token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle server errors (5xx) and connection issues.
    // ERR_NETWORK is what the browser reports (unreachable host, blocked by CORS);
    // ECONNREFUSED/ENOTFOUND only ever surface when axios runs under Node.
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
      console.error('Cannot connect to backend. Server may be unavailable.');
      const serverError = new Error('Server not available. Please try again later.');
      serverError.type = 'SERVER_UNAVAILABLE';
      return Promise.reject(serverError);
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.status);
      const serverError = new Error('Server error. Please try again later.');
      serverError.type = 'SERVER_ERROR';
      serverError.status = error.response.status;
      return Promise.reject(serverError);
    }

    // Handle CORS and other 403 errors
    if (error.response?.status === 403) {
      console.error('Forbidden: CORS or permission issue.');
      const forbiddenError = new Error('Access forbidden.');
      forbiddenError.type = 'FORBIDDEN';
      return Promise.reject(forbiddenError);
    }

    // Handle 404 errors
    if (error.response?.status === 404) {
      console.error('API endpoint not found.');
      const notFoundError = new Error('Resource not found.');
      notFoundError.type = 'NOT_FOUND';
      return Promise.reject(notFoundError);
    }

    // Handle timeout
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
      const timeoutError = new Error('Request timeout. Please try again.');
      timeoutError.type = 'TIMEOUT';
      return Promise.reject(timeoutError);
    }

    if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }

    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Lead API calls
export const getLeads = (storeIdFk, options = {}) => {
  const { signal, ...rest } = options;
  const params = { storeIdFk, ...rest };
  console.log('Making GET request to:', `${API_BASE_URL}/leads`, params);
  return api.get('/leads', { params, signal }).then(response => response.data);
};

export const getLeadById = (id) => {
  return api.get(`/leads/${id}`).then(response => response.data);
};

export const resolveLeadLink = (token) => {
  return api.get(`/lead-links/${token}`).then(response => response.data);
};

export const getLeadsByEnterprise = (enterpriseId, storeIdFk, options = {}) => {
  const { signal, ...rest } = options;
  const params = { storeIdFk: storeIdFk || undefined, ...rest };
  console.log('Making GET request to:', `${API_BASE_URL}/leads/enterprise/${enterpriseId}`, params);
  return api.get(`/leads/enterprise/${enterpriseId}`, { params, signal }).then(response => response.data);
};

export const getConversationByLeadId = (leadId) => {
  return api.get(`/conversations/${leadId}`).then(response => response.data);
};

export const sendMessage = (leadId, message, sendRealMessage = true) => {
  return api.post(`/conversations/${leadId}/messages`, { message, sendRealMessage }).then(response => response.data);
};

export const deleteLead = (id) => {
  return api.delete(`/leads/${id}`).then(response => response.data);
};

export const simulateCustomerMessage = (leadId, message, sendRealMessage = true) => {
  return api.post('/simulate-customer-message', { leadId, message, sendRealMessage }).then(response => response.data);
};

export const triggerInboundConversation = (phoneNumber, storeId, message, sendRealMessage = false) => {
  return api.post('/trigger-inbound-conversation', { phoneNumber, storeId, message, sendRealMessage }).then(response => response.data);
};

export const lookupDmsCustomers = (leadId) => {
  return api.get(`/dms/lookup/${leadId}`).then(response => response.data);
};

export const getStoresByEnterprise = (enterpriseId) => {
  return api.get(`/admin/stores/enterprise/${enterpriseId}`).then(response => response.data);
};

export const updateLeadRealSmsStatus = (leadId, sendRealMessage) => {
  return api.put(`/leads/${leadId}/real-sms`, { sendRealMessage }).then(response => response.data);
};

// Assistant admin API calls
export const getAssistants = () => {
  return api.get('/admin/assistants').then(response => response.data);
};

export const createAssistant = (assistant) => {
  return api.post('/admin/assistants', assistant).then(response => response.data);
};

export const updateAssistant = (id, assistant) => {
  return api.put(`/admin/assistants/${id}`, assistant).then(response => response.data);
};

export const deleteAssistant = (id) => {
  return api.delete(`/admin/assistants/${id}`).then(response => response.data);
};

export const toggleAssistantStatus = (id) => {
  return api.post(`/admin/assistants/${id}/toggle-status`).then(response => response.data);
};

// Enterprise admin API calls
export const getEnterprises = () => {
  return api.get('/admin/enterprises').then(response => response.data);
};

export const createEnterprise = (enterprise) => {
  return api.post('/admin/enterprises', enterprise).then(response => response.data);
};

export const updateEnterprise = (id, enterprise) => {
  return api.put(`/admin/enterprises/${id}`, enterprise).then(response => response.data);
};

export const deactivateEnterprise = (id) => {
  return api.delete(`/admin/enterprises/${id}`).then(response => response.data);
};

// True for local dev (`react-scripts start`) and deployed dev builds
// (`build:dev` -> env-cmd .env.development sets REACT_APP_ENV=development).
// NODE_ENV alone can't distinguish build:dev from build:prod since
// react-scripts always builds with NODE_ENV=production.
const isDevEnvironment = () =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENV === 'development';

export { api, getApiBaseUrl, isDevEnvironment };
export default api;
